import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import request from '../../utils/request';
import { 
  CalendarIcon,
  MapPinIcon,
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  CubeIcon,
  ShoppingBagIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Order data types
interface OrderItem {
  id: number;
  productId: number;
  quantity: number;
  unitPrice: number;
  image?: string;
  code?: string;
  designData?: any;
  product?: {
    id: number;
    name: string;
    description: string;
    image: string;
  };
}

interface Address {
  id: number;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone?: string;
}

interface Order {
  id: number;
  orderNumber: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
  items: OrderItem[];
  shippingAddress: Address;
  billingAddress?: Address;
  trackingNumber?: string;
  notes?: string;
}

// 售后相关接口
interface AfterSalesRequest {
  id: number;
  orderId: number;
  type: 'payment_issue' | 'refund' | 'exchange' | 'repair' | 'other';
  reason: string;
  description: string;
  status: 'pending' | 'processing' | 'resolved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  adminReply?: string;
  replies?: Array<{
    id: number;
    logType: 'customer_query' | 'platform_reply' | 'system';
    description: string;
    createdAt: string;
    updatedAt: string;
    user: string;
  }>;
}

// 从接口加载订单数据
const loadOrdersFromAPI = async (status?: string): Promise<Order[]> => {
  try {
    const params = new URLSearchParams();
    if (status && status !== 'all') {
      params.append('status', status);
    }
    
    const response = await request({
      url: `orders?${params.toString()}`,
      method: 'GET'
    });
    console.log('response..............', response);
    if (response.success && response.result) {
      return response.result.orders || [];
    }
    return [];
  } catch (error) {
    console.error('Error loading orders from API:', error);
    return [];
  }
};

const MyOrders: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  
  // 售后相关状态
  const [showAfterSalesModal, setShowAfterSalesModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [afterSalesRequests, setAfterSalesRequests] = useState<AfterSalesRequest[]>([]);
  const [newRequestType, setNewRequestType] = useState<'payment_issue' | 'refund' | 'exchange' | 'repair' | 'other'>('payment_issue');
  const [newRequestReason, setNewRequestReason] = useState('');
  const [newRequestDescription, setNewRequestDescription] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 处理支付按钮点击
  const handlePayNow = async (order: Order) => {
    try {
      // 调用订单确认API获取PayPal支付链接
      const confirmResponse: any = await request({
        url: 'orders/confirm',
        method: 'POST',
        data: {
          orderId: order.id
        }
      });

      if (confirmResponse.success) {
        const { approvalUrl, orderId: paypalOrderId } = confirmResponse.result;
        if (approvalUrl) {
          // 重定向到 PayPal 支付页面
          window.location.href = approvalUrl;
        } else {
          alert('Payment setup failed. Please contact support.');
        }
      } else {
        alert(confirmResponse.message || 'Failed to setup payment. Please try again.');
      }
    } catch (error: any) {
      console.error('Payment setup error:', error);
      alert('Failed to setup payment. Please try again.');
    }
  };

  // 打开售后弹窗
  const handleOpenAfterSales = (order: Order) => {
    setSelectedOrder(order);
    setShowAfterSalesModal(true);
    loadAfterSalesRequests(order.id);
  };

  // 关闭售后弹窗
  const handleCloseAfterSales = () => {
    setShowAfterSalesModal(false);
    setSelectedOrder(null);
    setAfterSalesRequests([]);
    setNewRequestType('refund');
    setNewRequestReason('');
    setNewRequestDescription('');
  };

  // 加载售后请求历史
  const loadAfterSalesRequests = async (orderId: number) => {
    try {
      const data = await request({
        method: 'GET',
        url: `orders/${orderId}/logs`
      });
      
      if (data.success && data.result.logs) {
        // 将订单日志转换为售后请求格式，支持对话线程
        const logs = data.result.logs;
        
        // 按 parentId 分组，创建对话线程
        const conversationMap = new Map();
        const rootLogs: any[] = [];
        
        // 首先处理所有日志
        logs.forEach((log: any) => {
          if (log.parentId === null) {
            // 根日志（客户查询）
            rootLogs.push(log);
            conversationMap.set(log.id, {
              id: log.id,
              orderId: log.orderId,
              type: log.metadata?.requestType || 
                    (log.action === 'refund_processed' ? 'refund' : 
                     log.action === 'cancelled' ? 'payment_issue' : 'other'),
              reason: log.metadata?.reason || log.description || `${log.action} request`,
              description: log.metadata?.description || log.description || `Order ${log.action} on ${new Date(log.createdAt).toLocaleString()}`,
              status: log.action === 'refund_processed' ? 'resolved' : 
                     log.action === 'cancelled' ? 'processing' : 
                     log.logType === 'customer_query' ? 'pending' : 'processing',
              createdAt: log.createdAt,
              updatedAt: log.updatedAt,
              logType: log.logType,
              replies: []
            });
          }
        });
        
        // 然后处理回复
        logs.forEach((log: any) => {
          if (log.parentId !== null && conversationMap.has(log.parentId)) {
            const parent = conversationMap.get(log.parentId);
            parent.replies.push({
              id: log.id,
              logType: log.logType,
              description: log.description,
              createdAt: log.createdAt,
              updatedAt: log.updatedAt,
              user: log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'
            });
            
            // 更新父级状态
            if (log.logType === 'platform_reply') {
              parent.status = 'processing';
            }
          }
        });
        
        // 转换为 AfterSalesRequest 格式
        const supportRequests: AfterSalesRequest[] = rootLogs.map(log => {
          const conversation = conversationMap.get(log.id);
          return {
            id: conversation.id,
            orderId: conversation.orderId,
            type: conversation.type,
            reason: conversation.reason,
            description: conversation.description,
            status: conversation.status,
            createdAt: conversation.createdAt,
            updatedAt: conversation.updatedAt,
            adminReply: conversation.replies.length > 0 ? 
                       conversation.replies[conversation.replies.length - 1].description : null,
            replies: conversation.replies
          };
        });

        setAfterSalesRequests(supportRequests);
      } else {
        setAfterSalesRequests([]);
      }
    } catch (error) {
      console.error('Failed to load after sales requests:', error);
      setAfterSalesRequests([]);
    }
  };

  // 提交新的售后请求
  const handleSubmitAfterSalesRequest = async () => {
    if (!selectedOrder || !newRequestReason.trim() || !newRequestDescription.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const data = await request({
        method: 'POST',
        url: `orders/${selectedOrder.id}/support-request`,
        data: {
          type: newRequestType,
          reason: newRequestReason,
          description: newRequestDescription
        }
      });

      if (data.success) {
        // 重新加载售后请求历史
        await loadAfterSalesRequests(selectedOrder.id);
        
        // 重置表单
        setNewRequestType('payment_issue');
        setNewRequestReason('');
        setNewRequestDescription('');
        
        alert('Support request submitted successfully! We will review it and get back to you soon.');
      } else {
        throw new Error(data.message || 'Failed to submit support request');
      }
    } catch (error) {
      console.error('Failed to submit support request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  // 加载订单数据
  useEffect(() => {
    const loadOrders = async () => {
      setIsLoading(true);
      try {
        const ordersData = await loadOrdersFromAPI(selectedStatus);
        setOrders(ordersData);
      } catch (error) {
        console.error('Failed to load orders:', error);
        // 如果 API 失败，使用 mock 数据作为备选
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrders();
  }, [selectedStatus]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return '';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'shipped':
        return <TruckIcon className="w-5 h-5" />;
      case 'processing':
        return <ClockIcon className="w-5 h-5" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5" />;
      case 'cancelled':
        return <ClockIcon className="w-5 h-5" />;
      default:
        return <ClockIcon className="w-5 h-5" />;
    }
  };

  // 由于数据已经从 API 按状态过滤，这里直接使用 orders
  const filteredOrders = orders;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
          <p className="text-gray-600">Track your orders and view order history</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <AccountSidebar currentPage="orders" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Order History</h2>
                <div className="flex items-center space-x-4">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  >
                    <option value="all">All Orders</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Orders List */}
            {isLoading ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading orders...</h3>
                <p className="text-gray-600">Please wait while we fetch your orders.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{order.orderNumber}</h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center space-x-1 text-sm text-gray-500">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{new Date(order.createdAt).toLocaleString()}</span>
                        </div>
                        <div 
                          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}
                          style={order.status === 'shipped' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                        >
                          {getStatusIcon(order.status)}
                          <span>{order.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">${order.totalAmount}</p>
                      <p className="text-sm text-gray-500">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-3 mb-4">
                    {order.items.map((item) => (
                      <div key={item.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.product?.name || 'Product'}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <CubeIcon className="w-6 h-6 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.product?.name || 'Product'}</p>
                          <p className="text-sm text-gray-500">Qty: {item.quantity} × ${item.unitPrice}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">${(item.quantity * item.unitPrice)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Shipping Info */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Shipping Address</h4>
                        <div className="flex items-start space-x-2">
                          <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div className="text-sm text-gray-600">
                            <p>{order.shippingAddress.firstName} {order.shippingAddress.lastName}</p>
                            <p>{order.shippingAddress.addressLine1}</p>
                            <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Tracking</h4>
                        {order.trackingNumber ? (
                          <div className="text-sm">
                            <p className="text-gray-600">Tracking Number:</p>
                            <p className="font-mono" style={{ color: '#2e3a47' }}>{order.trackingNumber}</p>
                            <button className="hover:opacity-80 text-sm mt-1" style={{ color: '#2e3a47' }}>
                              Track Package
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Tracking number will be available once shipped</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3 mt-4 pt-4 border-t border-gray-200"> 
                    {order.status === 'pending' ? (
                      <button 
                        onClick={() => handlePayNow(order)}
                        className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm"
                      >
                        Pay Now
                      </button>
                    ) : (
                      <button className="px-4 py-2 hover:bg-gray-50 rounded-lg text-sm" style={{ color: '#2e3a47' }}>
                        Download Invoice
                      </button>
                    )}
                    {order.status === 'delivered' && (
                      <button className="px-4 py-2 text-white rounded-lg transition-colors text-sm"
                        style={{ backgroundColor: '#2e3a47' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}>
                        Reorder
                      </button>
                    )}
                    <button 
                      onClick={() => handleOpenAfterSales(order)}
                      className="px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg text-sm flex items-center space-x-1"
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                      <span>Support</span>
                    </button>
                  </div>
                </div>
              ))}

                {filteredOrders.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <div className="flex justify-center mb-4">
                      <ShoppingBagIcon className="w-16 h-16 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No orders found</h3>
                    <p className="text-gray-600 mb-6">
                      {selectedStatus === 'all' 
                        ? "You haven't placed any orders yet." 
                        : `No ${selectedStatus.toLowerCase()} orders found.`}
                    </p>
                    <Link
                      to="/products"
                      className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                      style={{ backgroundColor: '#2e3a47' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                    >
                      Start Shopping
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* After Sales Modal */}
      {showAfterSalesModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 999 }}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Order Support</h2>
                <p className="text-sm text-gray-600">Order #{selectedOrder.orderNumber}</p>
              </div>
              <button
                onClick={handleCloseAfterSales}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex h-[calc(90vh-120px)]">
              {/* Left Side - Request History */}
              <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Request History</h3>
                  
                  {afterSalesRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No after-sales requests yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {afterSalesRequests.map((request) => (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span 
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                request.status === 'processing' ? '' :
                                request.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}
                              style={request.status === 'processing' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                            >
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <div className="mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              {request.type.charAt(0).toUpperCase() + request.type.slice(1)} - {request.reason}
                            </span>
                          </div>
                          
                          {/* 客户原始请求 */}
                          <div className="mb-3">
                            <div className="flex items-start space-x-2">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#f0f2f5' }}>
                                <span className="text-xs font-medium" style={{ color: '#2e3a47' }}>C</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-600">{request.description}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(request.createdAt).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* 对话回复 */}
                          {request.replies && request.replies.length > 0 && (
                            <div className="space-y-3">
                              {request.replies.map((reply) => (
                                <div key={reply.id} className="flex items-start space-x-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    reply.logType === 'platform_reply' ? 'bg-green-100' : 'bg-gray-100'
                                  }`}>
                                    <span className={`text-xs font-medium ${
                                      reply.logType === 'platform_reply' ? 'text-green-600' : 'text-gray-600'
                                    }`}>
                                      {reply.logType === 'platform_reply' ? 'A' : 'S'}
                                    </span>
                                  </div>
                                  <div className="flex-1">
                                    <div className={`rounded-lg p-3 ${
                                      reply.logType === 'platform_reply' ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                                    }`}>
                                      <p className="text-sm text-gray-700">{reply.description}</p>
                                      <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-gray-500">
                                          {reply.logType === 'platform_reply' ? 'Admin' : 'System'} - {reply.user}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                          {new Date(reply.createdAt).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* 兼容旧的 adminReply 显示 */}
                          {request.adminReply && (!request.replies || request.replies.length === 0) && (
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-xs font-medium text-gray-700 mb-1">Admin Reply:</p>
                              <p className="text-sm text-gray-600">{request.adminReply}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - New Request Form */}
              <div className="w-1/2 overflow-y-auto">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Submit New Request</h3>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handleSubmitAfterSalesRequest(); }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Request Type *
                      </label>
                      <select
                        value={newRequestType}
                        onChange={(e) => setNewRequestType(e.target.value as 'payment_issue' | 'refund' | 'exchange' | 'repair' | 'other')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      >
                        <option value="payment_issue">Payment Issue</option>
                        <option value="refund">Refund</option>
                        <option value="exchange">Exchange</option>
                        <option value="repair">Repair</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason *
                      </label>
                      <input
                        type="text"
                        value={newRequestReason}
                        onChange={(e) => setNewRequestReason(e.target.value)}
                        placeholder="Brief reason for your request"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={newRequestDescription}
                        onChange={(e) => setNewRequestDescription(e.target.value)}
                        placeholder="Please provide detailed information about your request..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        required
                      />
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        type="submit"
                        className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        Submit Request
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseAfterSales}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders; 